import React, { useState, useEffect } from 'react';
import { Play, RefreshCw, Info } from 'lucide-react';
import ClauseCard from './components/ClauseCard';
import VariableAssignment from './components/VariableAssignment';
import ExampleFormulas from './components/ExampleFormulas';
import FormulaStatusPanel from './components/FormulaStatusPanel';
import { CNFUtils } from './utils/CNFUtils';

const SATVisualizer = () => {
  const [clauses, setClauses] = useState([]);
  const [formulaInput, setFormulaInput] = useState('');
  const [assignment, setAssignment] = useState({});
  const [variables, setVariables] = useState([]);
  const [showExamples, setShowExamples] = useState(true);

  useEffect(() => {
    const vars = CNFUtils.getAllVariables(clauses);
    setVariables(vars);
    const newAssignment = {};
    vars.forEach(v => { newAssignment[v] = assignment[v] !== undefined ? assignment[v] : false; });
    setAssignment(newAssignment);
  }, [clauses]);

  const parseFormula = () => {
    if (!formulaInput.trim()) return;
    try {
      const parsedClauses = CNFUtils.parseFormula(formulaInput);
      if (parsedClauses.length > 0) { setClauses(parsedClauses); setShowExamples(false); }
    } catch (error) { alert('Error parsing formula. Please check the syntax.'); }
  };

  const selectExample = (formula) => {
    setFormulaInput(formula);
    const parsedClauses = CNFUtils.parseFormula(formula);
    setClauses(parsedClauses);
    setShowExamples(false);
  };

  const deleteClause = (index) => { setClauses(clauses.filter((_, i) => i !== index)); };
  const reset = () => { setClauses([]); setFormulaInput(''); setAssignment({}); setShowExamples(true); };
  const resetAllVariables = () => { const newAssignment = {}; variables.forEach(v => { newAssignment[v] = false; }); setAssignment(newAssignment); };
  const randomizeVariables = () => { const newAssignment = {}; variables.forEach(v => { newAssignment[v] = Math.random() > 0.5; }); setAssignment(newAssignment); };
  const allSatisfied = clauses.length > 0 && clauses.every(clause => CNFUtils.evaluateClause(clause.literals, assignment));

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-6xl mx-auto">
        <header className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">SAT CNF Formula Visualizer</h1>
          <p className="text-gray-600">Visualize Boolean satisfiability problems in Conjunctive Normal Form</p>
        </header>

        <div className="grid lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-lg shadow-md p-4">
              <h2 className="font-bold text-xl mb-4 text-gray-800">Enter CNF Formula</h2>
              <div className="mb-4">
                <div className="bg-gray-50 border border-gray-300 rounded-lg p-3 mb-3 text-sm">
                  <div className="font-semibold mb-2">Supported Notation:</div>
                  <div className="space-y-1 text-gray-700">
                    <div><strong>Variables:</strong> A, B, x1, etc.</div>
                    <div><strong>Negation:</strong> ~, ¬, !</div>
                    <div><strong>OR:</strong> or, |, ∨</div>
                    <div><strong>AND:</strong> and, &, ∧</div>
                  </div>
                </div>
                <textarea value={formulaInput} onChange={(e) => setFormulaInput(e.target.value)} placeholder="(A or ~B) and (~A or C) and (B)" className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 font-mono text-sm" rows="3" />
                <div className="flex gap-2 mt-3">
                  <button onClick={parseFormula} className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors font-semibold"><Play size={18} />Parse Formula</button>
                  <button onClick={reset} className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"><RefreshCw size={18} />Reset</button>
                </div>
              </div>
              {clauses.length > 0 && (
                <div className="border-t pt-4">
                  <h3 className="font-bold text-lg mb-3 text-gray-800">Clauses (connected by ∧)</h3>
                  <div className="space-y-3">
                    {clauses.map((clause, index) => (
                      <ClauseCard key={index} clause={clause} index={index} assignment={assignment} onDelete={deleteClause} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="space-y-4">
            {showExamples && <ExampleFormulas onSelectExample={selectExample} />}
            {clauses.length > 0 && <FormulaStatusPanel allSatisfied={allSatisfied} />}
          </div>
        </div>

        {variables.length > 0 && (
          <VariableAssignment variables={variables} assignment={assignment} onAssignmentChange={setAssignment} onRandomize={randomizeVariables} onResetAll={resetAllVariables} />
        )}

        {clauses.length > 0 && (
          <div className="mt-6 bg-indigo-50 border-2 border-indigo-300 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="text-indigo-600 flex-shrink-0 mt-1" size={20} />
              <p className="text-sm text-indigo-900"><strong>How it works:</strong> A clause is TRUE if at least one literal evaluates to TRUE. The formula is SATISFIED when ALL clauses are TRUE.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SATVisualizer;